/**
 * @fileoverview Product showcase — format tab card
 *
 * Tab switching is handled by <tabs-element> (component-tabs.js). This adds the
 * parts that depend on Shopify data:
 *
 * 1. Variant selection — active state, price, and CTA target
 * 2. Tab change — address bar update, PDP context only
 * 3. Add to cart — dispatches `cart:add` for the 'cart' CTA action. assets/cart.js owns
 *    the request; this file only owns the button and status-line state around it.
 *
 * Work is scoped to each [data-product-showcase] container so multiple showcases
 * on a page, and the editor's isolated section re-render, both behave. Prices come
 * pre-split off the variant buttons — no money formatting happens here.
 */

document.addEventListener("DOMContentLoaded", function () {
  document
    .querySelectorAll("[data-product-showcase]")
    .forEach(initProductShowcase);

  /**
   * CTAs waiting on a cart:add, keyed by the correlation token dispatched with the command.
   * A product page can hold more than one showcase (product.json renders both main-product
   * and product-information), so two CTAs can be in flight at once. cart.js echoes each
   * command's token on its result event, so a result maps straight back to the CTA that
   * started it — no reliance on event ordering, and a stray error from some other command
   * (a different token) is ignored rather than releasing the wrong button.
   * @type {Map<*, { cta: HTMLElement, panel: HTMLElement, container: HTMLElement }>}
   */
  const pending = new Map();
  let tokenSeq = 0;

  /**
   * Releases a CTA and shows its outcome.
   * @param {{ cta: HTMLElement, panel: HTMLElement, container: HTMLElement }} ctx
   * @param {string} [message] - status text; omit to release the button without a message
   * @param {boolean} [isError]
   */
  function finishAdd(ctx, message, isError) {
    ctx.cta.disabled = false;
    ctx.cta.removeAttribute("aria-busy");
    if (message) setStatus(ctx.panel, message, isError);
  }

  document.addEventListener("cart:updated", (e) => {
    if (e.detail?.source !== "add") return;
    const ctx = pending.get(e.detail.token);
    if (!ctx) return;
    pending.delete(e.detail.token);
    finishAdd(ctx, ctx.container.dataset.textAdded, false);
  });

  document.addEventListener("cart:error", (e) => {
    const ctx = pending.get(e.detail?.token);
    if (!ctx) return;
    pending.delete(e.detail.token);
    finishAdd(ctx, ctx.container.dataset.textError, true);
  });

  // Safety net: when the queue drains, release any CTA whose token never came back (e.g. a
  // command cart.js dropped before it produced a result) so a button can never stick.
  document.addEventListener("cart:loading", (e) => {
    if (e.detail?.loading !== false || pending.size === 0) return;
    pending.forEach((ctx) => finishAdd(ctx));
    pending.clear();
  });

  /** @param {HTMLElement} container - the [data-product-showcase] wrapper */
  function initProductShowcase(container) {
    const ctaAction = container.dataset.ctaAction || "link";
    const updatesUrl = container.dataset.updateUrl === "true";

    container.addEventListener("click", (e) => {
      const variantButton = e.target.closest("[data-showcase-variant]");
      if (variantButton) {
        selectVariant(variantButton);
        return;
      }

      const cta = e.target.closest("[data-showcase-cta]");
      if (cta && ctaAction === "cart") {
        e.preventDefault();
        addToCart(cta);
      }
    });

    container.addEventListener("tabs:change", (e) => {
      // A message from the previously active format doesn't apply to this one.
      container
        .querySelectorAll("[data-showcase-panel]")
        .forEach((panel) => setStatus(panel, ""));

      if (!updatesUrl) return;

      const panel = container.querySelector(
        `[data-showcase-panel][value="${e.detail.value}"]`,
      );
      if (panel) updateAddressBar(panel);
    });

    function selectVariant(button) {
      const panel = button.closest("[data-showcase-panel]");
      if (!panel || button.disabled) return;

      panel
        .querySelectorAll("[data-showcase-variant]")
        .forEach((el) => el.classList.remove("is-active"));
      button.classList.add("is-active");

      panel.dataset.variantId = button.dataset.variantId;

      const priceMain = panel.querySelector("[data-showcase-price-main]");
      const priceCents = panel.querySelector("[data-showcase-price-cents]");
      const priceCompare = panel.querySelector("[data-showcase-price-compare]");
      const compare = button.dataset.compare || "";

      if (priceMain) priceMain.textContent = button.dataset.priceMain || "";
      if (priceCents) priceCents.textContent = button.dataset.priceCents || "";

      if (priceCompare) {
        priceCompare.textContent = compare;
        priceCompare.hidden = compare === "";
      }

      const cta = panel.querySelector("[data-showcase-cta]");
      if (cta && ctaAction === "link" && button.dataset.variantUrl) {
        cta.href = button.dataset.variantUrl;
      }

      setStatus(panel, "");

      if (updatesUrl) updateAddressBar(panel);
    }

    /** Keeps a copied link matching what is on screen. */
    function updateAddressBar(panel) {
      const productUrl = panel.dataset.productUrl;
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      if (panel.dataset.variantId) {
        url.searchParams.set("variant", panel.dataset.variantId);
      }

      history.replaceState({}, "", url);
    }

    /**
     * assets/cart.js owns every cart request — it serialises them and opens the drawer on
     * success. This only dispatches the command and holds the button state until one of the
     * result events comes back.
     * @param {HTMLElement} cta - the clicked [data-showcase-cta]
     */
    function addToCart(cta) {
      const panel = cta.closest("[data-showcase-panel]");
      if (!panel) return;

      const variantId = panel.dataset.variantId;
      if (!variantId) {
        setStatus(panel, container.dataset.textError, true);
        return;
      }

      cta.disabled = true;
      cta.setAttribute("aria-busy", "true");
      setStatus(panel, "");

      const token = `showcase-${(tokenSeq += 1)}`;
      pending.set(token, { cta, panel, container });
      document.dispatchEvent(
        new CustomEvent("cart:add", {
          detail: { id: Number(variantId), quantity: 1, token },
        }),
      );
    }
  }

  /**
   * @param {HTMLElement} panel - panel owning the status line
   * @param {string} message - text to show, empty to clear
   * @param {boolean} [isError] - styles the message as an error
   */
  function setStatus(panel, message, isError) {
    const status = panel.querySelector("[data-showcase-status]");
    if (!status) return;

    status.textContent = message || "";
    status.classList.toggle("product-showcase__status--error", Boolean(isError));
  }
});
